import React, { useEffect, useState } from 'react';
import TrainerLayout from '../../components/trainer/TrainerLayout';
import { getTrainerEarnings, getTrainerPayouts, requestPayout } from '../../api/client';
import { useToast } from '../../contexts/ToastContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { DollarSign, TrendingUp, Wallet, Loader2 } from 'lucide-react';

export default function TrainerEarnings(): React.JSX.Element {
  const toast = useToast();
  const [earnings, setEarnings] = useState<any[]>([]);
  const [payouts, setPayouts] = useState<any[]>([]);
  const [summary, setSummary] = useState({ total_earned: 0, last_30_days: 0 });
  const [loading, setLoading] = useState(true);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [earningsRes, payoutsRes] = await Promise.all([
        getTrainerEarnings(),
        getTrainerPayouts(),
      ]);
      setEarnings(earningsRes.earnings || []);
      setSummary(earningsRes.summary || { total_earned: 0, last_30_days: 0 });
      setPayouts(payoutsRes.payouts || []);
    } catch (err: any) {
      toast.show(err.message || 'Failed to load earnings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePayoutRequest = async () => {
    const amount = parseFloat(payoutAmount);
    if (!amount || amount <= 0) {
      toast.show('Enter a valid amount', 'error');
      return;
    }
    setRequesting(true);
    try {
      await requestPayout({ amount, payment_method: 'bank_transfer' });
      toast.show('Payout requested successfully', 'success');
      setPayoutAmount('');
      loadData();
    } catch (err: any) {
      toast.show(err.message || 'Failed to request payout', 'error');
    } finally {
      setRequesting(false);
    }
  };

  const availableBalance = summary.total_earned - payouts
    .filter((p: any) => ['pending', 'processing', 'paid'].includes(p.status))
    .reduce((sum: number, p: any) => sum + parseFloat(p.amount), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <TrainerLayout>
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-slate-900 mb-6">Earnings & Payouts</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
            <DollarSign className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <p className="text-sm text-slate-500">Total Earned</p>
            <p className="text-xl font-bold text-slate-900">₹{summary.total_earned.toLocaleString()}</p>
          </div>
        </Card>
        <Card className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
            <TrendingUp className="w-6 h-6 text-amber-500" />
          </div>
          <div>
            <p className="text-sm text-slate-500">Last 30 Days</p>
            <p className="text-xl font-bold text-slate-900">₹{summary.last_30_days.toLocaleString()}</p>
          </div>
        </Card>
        <Card className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
            <Wallet className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <p className="text-sm text-slate-500">Available Balance</p>
            <p className="text-xl font-bold text-slate-900">₹{availableBalance.toLocaleString()}</p>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Request Payout</h2>
          <div className="flex gap-3">
            <input
              type="number"
              className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
              placeholder="Amount"
              value={payoutAmount}
              onChange={(e) => setPayoutAmount(e.target.value)}
            />
            <Button onClick={handlePayoutRequest} disabled={requesting || availableBalance <= 0}>
              {requesting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Request'}
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-2">Minimum payout threshold may apply.</p>
        </Card>

        <Card>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Payout History</h2>
          {payouts.length === 0 ? (
            <p className="text-slate-500 text-sm">No payout requests yet.</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {payouts.map((p: any) => (
                <div key={p.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <div>
                    <p className="font-medium text-slate-900">₹{parseFloat(p.amount).toLocaleString()}</p>
                    <p className="text-xs text-slate-500">{new Date(p.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                    p.status === 'paid' ? 'bg-green-100 text-green-700' :
                    p.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                    p.status === 'rejected' ? 'bg-red-100 text-red-700' :
                    'bg-amber-100 text-amber-600'
                  }`}>
                    {p.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card className="mt-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Earning History</h2>
        {earnings.length === 0 ? (
          <p className="text-slate-500 text-sm">No earnings yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-3 text-slate-500 font-medium">Course</th>
                  <th className="text-right py-2 px-3 text-slate-500 font-medium">Amount</th>
                  <th className="text-right py-2 px-3 text-slate-500 font-medium">Fee</th>
                  <th className="text-right py-2 px-3 text-slate-500 font-medium">Net</th>
                  <th className="text-right py-2 px-3 text-slate-500 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {earnings.map((e: any) => (
                  <tr key={e.id} className="border-b border-slate-100 last:border-0">
                    <td className="py-2 px-3 text-slate-900">{e.course_title}</td>
                    <td className="py-2 px-3 text-right text-slate-900">₹{parseFloat(e.amount).toLocaleString()}</td>
                    <td className="py-2 px-3 text-right text-slate-500">₹{parseFloat(e.platform_fee).toLocaleString()}</td>
                    <td className="py-2 px-3 text-right font-medium text-green-600">₹{parseFloat(e.net_earning).toLocaleString()}</td>
                    <td className="py-2 px-3 text-right text-slate-500">{new Date(e.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
    </TrainerLayout>
  );
}
